"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "./auth-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Blocks, Info, LogOut, MessageSquare, Settings, Shield, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { VersionInfoDialog } from "./version-info-dialog";

const emptySubscribe = () => () => {};

export function UserMenu() {
  const { user, signOut, isLoading, isAdmin } = useAuth();
  const router = useRouter();
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  // SSR-safe mount detection without triggering cascading renders
  const mounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

  // Derive feedback state from localStorage (page reloads on toggle)
  const feedbackEnabled = mounted
    ? localStorage.getItem("feedbackEnabled") === "true"
    : false;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (mounted && !isLoading && !user) {
      router.push("/login");
    }
  }, [mounted, isLoading, user, router]);

  // Consistent loading state for SSR/hydration
  if (!mounted || isLoading) {
    return (
      <Button
        variant="ghost"
        className="relative h-7 w-7 rounded-full p-0"
        disabled
      >
        <Avatar className="h-7 w-7">
          <AvatarFallback className="bg-white/[0.06] text-[10px] text-white/40" />
        </Avatar>
      </Button>
    );
  }

  if (!user) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
      router.push("/login");
      router.refresh();
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  const toggleFeedback = () => {
    const newState = !feedbackEnabled;
    localStorage.setItem("feedbackEnabled", String(newState));
    toast.success(`Feedback widget ${newState ? "enabled" : "disabled"}`);
    // Reload to apply changes
    window.location.reload();
  };

  const getInitials = (email: string) => {
    return email
      .split("@")[0]
      .split(".")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Support both local auth (direct properties) and Supabase auth (user_metadata)
  const avatarUrl =
    user.avatar_url || user.user_metadata?.avatar_url || "/avatar/default.png";
  const displayName = user.name || user.user_metadata?.full_name || "User";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-7 w-7 rounded-full p-0 ring-1 ring-white/[0.06] transition-all duration-150 hover:ring-white/[0.12]"
        >
          <Avatar className="h-7 w-7">
            <AvatarImage src={avatarUrl} alt={user.email} />
            <AvatarFallback className="bg-white/[0.06] text-[10px] font-medium text-white/40">
              {getInitials(user.email || "U")}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={() => router.push("/settings")}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Global Settings</span>
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuItem onClick={() => router.push("/hive-user")}>
              <Users className="mr-2 h-4 w-4" />
              <span>Hive Users</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setVersionDialogOpen(true);
          }}
        >
          <Info className="mr-2 h-4 w-4" />
          <span>Release Notes</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
      <VersionInfoDialog
        open={versionDialogOpen}
        onOpenChange={setVersionDialogOpen}
      />
    </DropdownMenu>
  );
}
